import {
  Business as BuildingIcon,
  Layers as FloorIcon,
  Room as RoomIcon,
  LocationOn as SpaceIcon,
  SquareFoot as OtherSpacesIcon,
} from '@mui/icons-material';
import { Cascader, CascaderOption } from '@nohara-dxdev/react-core';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { BuildingStructure, FloorWithRooms, RoomEntity } from '@/types/api';
import { Location, LocationType, OTHER_SPACES_MARKER } from '@/types/location';

import { useBuildings } from '../api/get-buildings';
import { useRoomFilterValues } from '../providers/room-filter-provider';

interface LocationAssignmentSelectProps {
  location: Location | null;
  onChange: (location: Location | null) => void;
  onSelect?: (location: Location | null) => void;
  onClear?: () => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  excludeLocationIds?: string[];
}

export const LocationAssignmentSelect = ({
  location,
  onChange,
  onSelect,
  onClear,
  error,
  placeholder,
  disabled,
  excludeLocationIds = [],
}: LocationAssignmentSelectProps) => {
  const { t } = useTranslation();
  const {
    data: buildings = [],
    isLoading: isLoadingBuildings,
    error: errorLoadingBuildings,
  } = useBuildings();
  const { floorIdFilter } = useRoomFilterValues();

  // Transform building data into Cascader format with filtering
  // Only show rooms, outdoor, and otherSpaces (not buildings or floors)
  const cascaderOptions = useMemo(() => {
    return buildings
      .map((building: BuildingStructure): CascaderOption | null => {
        // Build floors options with rooms
        const filteredFloors = building.floors.map(
          (floor: FloorWithRooms): CascaderOption => ({
            id: floor.id,
            label: floor.name,
            value: floor,
            icon: <FloorIcon />,
            disabled: Boolean(floorIdFilter && floor.id !== floorIdFilter),
            children: floor.rooms.map(
              (room: RoomEntity): CascaderOption => ({
                id: room.id,
                label: `${room.name} (${room.roomKey})`,
                value: room,
                icon: <RoomIcon />,
                disabled: excludeLocationIds.includes(room.id),
              }),
            ),
          }),
        );

        // Build Other Spaces option (first in the list)
        const otherSpacesChildren: CascaderOption[] = [];

        // Add Outdoor first if it exists
        if (building.outdoor) {
          otherSpacesChildren.push({
            id: building.outdoor.id,
            label: `${t('buildings.outdoor')} (${building.outdoor.spaceKey})`,
            value: building.outdoor,
            icon: <SpaceIcon />,
            disabled: excludeLocationIds.includes(building.outdoor.id),
          });
        }

        // Add other spaces
        for (const space of building.otherSpaces) {
          otherSpacesChildren.push({
            id: space.id,
            label: `${space.name} (${space.spaceKey})`,
            value: space,
            icon: <SpaceIcon />,
            disabled: excludeLocationIds.includes(space.id),
          });
        }

        // Create Other Spaces option (only if there are spaces to show)
        const otherSpacesOption: CascaderOption | null =
          otherSpacesChildren.length > 0
            ? {
                id: OTHER_SPACES_MARKER,
                label: t('buildings.otherSpaces'),
                value: OTHER_SPACES_MARKER,
                icon: <OtherSpacesIcon />,
                children: otherSpacesChildren,
              }
            : null;

        // Combine floors and Other Spaces (Other Spaces first)
        const allFloorOptions: CascaderOption[] = [];
        if (otherSpacesOption) {
          allFloorOptions.push(otherSpacesOption);
        }
        allFloorOptions.push(...filteredFloors);

        // Only include building if it has floors or other spaces matching the filter
        if (allFloorOptions.length === 0) return null;

        return {
          id: building.id,
          label: building.name,
          value: building,
          icon: <BuildingIcon />,
          children: allFloorOptions,
        };
      })
      .filter((building): building is CascaderOption => building !== null);
  }, [buildings, floorIdFilter, excludeLocationIds, t]);

  // Find selected path based on location
  const selectedPath = useMemo(() => {
    if (!location) return [];

    // Find the location in the cascader structure
    for (const building of cascaderOptions) {
      // Check rooms
      for (const floor of building.children ?? []) {
        if (floor.id === OTHER_SPACES_MARKER) {
          // Check outdoor and otherSpaces
          const space = floor.children?.find((s) => s.id === location.id);
          if (space) {
            return [building, floor, space];
          }
        } else {
          // Check rooms
          const room = floor.children?.find((r) => r.id === location.id);
          if (room) {
            return [building, floor, room];
          }
        }
      }
    }
    return [];
  }, [location, cascaderOptions]);

  // Handle cascader selection changes
  const handleCascaderChange = useCallback(
    (value: CascaderOption[]) => {
      // Only trigger when a specific location (room, outdoor, or otherSpace) is selected
      if (value.length !== 3) {
        return false; // Don't trigger onChange for building or floor selection
      }

      const spaceOrRoom = value[2];
      const spaceOrRoomId = spaceOrRoom.id;
      if (typeof spaceOrRoomId !== 'string') return false;

      const floorOrOtherSpaces = value[1];
      const floorOrOtherSpacesId = floorOrOtherSpaces.id;

      let selectedLocation: Location | null = null;

      if (floorOrOtherSpacesId === OTHER_SPACES_MARKER) {
        // Determine if it's outdoor or otherSpace
        const spaceValue = spaceOrRoom.value;
        if (spaceValue && typeof spaceValue === 'object' && 'spaceKey' in spaceValue) {
          // Check if it's outdoor (no name field) or otherSpace (has name field)
          if ('name' in spaceValue) {
            selectedLocation = {
              id: spaceOrRoomId,
              type: LocationType.OTHER_SPACES,
            };
          } else {
            selectedLocation = {
              id: spaceOrRoomId,
              type: LocationType.OUTDOOR,
            };
          }
        } else {
          // Fallback - assume otherSpaces
          selectedLocation = {
            id: spaceOrRoomId,
            type: LocationType.OTHER_SPACES,
          };
        }
      } else if (typeof floorOrOtherSpacesId === 'string') {
        // Room selected
        selectedLocation = {
          id: spaceOrRoomId,
          type: LocationType.ROOM,
        };
      }

      if (selectedLocation) {
        onChange(selectedLocation);
        onSelect?.(selectedLocation);
      }
      return true;
    },
    [onChange, onSelect],
  );

  const searchTextExtractor = useCallback((option: CascaderOption) => {
    return option.label;
  }, []);

  return (
    <Cascader
      options={cascaderOptions}
      value={selectedPath}
      onChange={handleCascaderChange}
      placeholder={placeholder ?? t('rooms.nested.selectRoom')}
      loading={isLoadingBuildings}
      error={errorLoadingBuildings ? t('rooms.nested.errorLoadingBuildings') : error}
      disabled={disabled}
      columnHeaders={[
        t('rooms.nested.buildings'),
        t('rooms.nested.floors'),
        t('rooms.nested.rooms'),
      ]}
      emptyText={t('rooms.nested.noBuildings')}
      maxHeight={400}
      minHeight={200}
      searchable={true}
      searchTextExtractor={searchTextExtractor}
      searchEmptyText={t('rooms.nested.noSearchResults')}
      onClear={onClear}
    />
  );
};
